import type { DateTime } from 'luxon'
import { computed, makeObservable, observable } from 'mobx'
import {
  createClickWrap,
  getClickWrapList,
  updateClickWrap,
} from '../firestore/ClickWrap'
import { ClickWrap } from '../models/ClickWrap'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import type { ClickWrapRole } from '../types'
import { ClickWrapState, type StaticModelCollection } from '../types'
import { Cubit } from './core'
import { SettingsTermsAndConditions } from '../models/SettingsTermsAndConditions'
import { getSettingsTermsAndConditionsDoc } from '../firestore/SettingsTermsAndConditons.ts'

export class AdminDashboardClickWrapsCubit extends Cubit {
  repository: FirebaseRepository

  clickWraps: StaticModelCollection<ClickWrap>

  settingsTermsAndConditions: SettingsTermsAndConditions

  @observable
  sortOrder: 'asc' | 'desc' = 'asc'

  @observable
  sortingStrategy: 'name' | 'date' | 'roles' = 'name'

  constructor(repository: FirebaseRepository) {
    super()
    this.repository = repository
    this.clickWraps = ClickWrap.emptyCollection(this.repository)
    this.settingsTermsAndConditions = SettingsTermsAndConditions.empty(
      this.repository
    )
    makeObservable(this)
  }

  initialize(): void {
    this.addStream(getClickWrapList(this.repository), (models) =>
      this.clickWraps.replaceModels(models)
    )
    this.addStream(getSettingsTermsAndConditionsDoc(this.repository), (model) =>
      this.settingsTermsAndConditions.replaceModel(model)
    )
  }

  @computed
  get visibleClickWraps(): ClickWrap[] {
    return this.clickWraps.models
      .filter(
        (clickWrap) =>
          clickWrap.data.clickWrapState === ClickWrapState.active ||
          clickWrap.data.clickWrapState === ClickWrapState.draft
      )
      .sort((a, b) => {
        const directionMod = this.sortOrder === 'asc' ? 1 : -1
        switch (this.sortingStrategy) {
          case 'name':
            return (
              this.repository.utils.compareStrings(
                a.data.clickWrapName,
                b.data.clickWrapName
              ) * directionMod
            )
          case 'date':
            return (
              (a.data.clickWrapDate.getTime() -
                b.data.clickWrapDate.getTime()) *
              directionMod
            )
          case 'roles': {
            const aRolesSort = a.data.clickWrapRoles.slice().sort().join(',')
            const bRolesSort = b.data.clickWrapRoles.slice().sort().join(',')
            return (
              this.repository.utils.compareStrings(aRolesSort, bRolesSort) *
              directionMod
            )
          }
        }
      })
  }

  async createClickWrap(data: {
    clickWrapName: string
    clickWrapRoles: ClickWrapRole[]
    clickWrapDescription: string
    clickWrapUrl: string
    clickWrapDate: DateTime
  }): Promise<void> {
    await createClickWrap(this.repository, {
      clickWrapName: data.clickWrapName,
      clickWrapRoles: data.clickWrapRoles,
      clickWrapDescription: data.clickWrapDescription,
      clickWrapUrl: data.clickWrapUrl,
      clickWrapDate: data.clickWrapDate,
    })
  }

  async updateClickWrap(
    clickWrapId: string,
    data: {
      clickWrapName: string
      clickWrapRoles: ClickWrapRole[]
      clickWrapDescription: string
      clickWrapUrl: string
      clickWrapDate: DateTime
    }
  ): Promise<void> {
    await updateClickWrap(this.repository, { clickWrapId }, data)
  }

  async archiveClickWrap(clickWrapId: string): Promise<void> {
    if (
      this.settingsTermsAndConditions.data?.preMeetingClickWrapId ===
      clickWrapId
    ) {
      throw new Error(
        'Cannot archive the pre-meeting terms and conditions linked in settings'
      )
    }
    await updateClickWrap(
      this.repository,
      { clickWrapId },
      { clickWrapState: ClickWrapState.archived }
    )
  }
}
